import { useState } from 'react';
import ModalHeader from '../components/ModalHeader';
import ErrorMessage from '../../components/ui/ErrorMessage';
import ActionStatusText from '../../components/ui/ActionStatusText';
import useSaveOwnExercise from '../../hooks/data/exercises/useSaveOwnExercise';
import { useModalNavigation } from '../../context/ModalControllerContext';
import { getNowHHmm } from '../../helpers';
import { formatDuration, formatExercisePrefix } from '../../helpers/domain';

const ModalOwnExercise = ({ currentDay, savedExercises, onAddSavedExercise }) => {
    const { close, goBack } = useModalNavigation();

    const [isManualAdd, setIsManualAdd] = useState(false);
    const [exerciseForm, setExerciseForm] = useState({
        name: '',
        duration: '',
        kcal: '',
        time: getNowHHmm(),
        comment: ''
    });

    const { saveOwnExercise, isSaving, isSuccess, isError, error } = useSaveOwnExercise({
        currentDay,
        exerciseForm,
        onAddSavedExercise,
        setExerciseForm,
        setIsManualAdd
    });

    const isProcessing = isSaving || isSuccess;
    const isAddingExercise = isManualAdd || savedExercises.length === 0;

    return (
        <>
            <ModalHeader
                title="Lägg till egen träning"
                onClose={close}
                onGoBack={goBack}
                disableButtons={isSaving}
            />

            <div className="modal-body own-exercise">
                {!isError ? (
                    isAddingExercise ? (
                        <div className="own-exercise-input-wrapper">
                            {/* Exercise name */}
                            <div className="input-with-label">
                                <span className="inline-label">Namn:</span>
                                <input
                                    type="text"
                                    id="own-exercise-name"
                                    className="modal-input name-input"
                                    value={exerciseForm.name}
                                    onChange={(e) =>
                                        setExerciseForm((prev) => ({
                                            ...prev,
                                            name: e.target.value
                                        }))
                                    }
                                    disabled={isProcessing}
                                    placeholder="Valfritt"
                                />
                            </div>

                            {/* Duration (minutes) */}
                            <div className="input-with-label">
                                <span className="inline-label">Tid (min):</span>
                                <input
                                    type="number"
                                    id="own-exercise-duration"
                                    className="modal-input duration-input"
                                    min="1"
                                    step="1"
                                    value={exerciseForm.duration}
                                    onChange={(e) =>
                                        setExerciseForm((prev) => ({
                                            ...prev,
                                            duration: e.target.value
                                        }))
                                    }
                                    disabled={isProcessing}
                                    placeholder="Valfritt"
                                />
                            </div>

                            {/* Kcal burned */}
                            <div className="input-with-label">
                                <span className="inline-label">Kcal:</span>
                                <input
                                    type="number"
                                    id="own-exercise-kcal"
                                    className="modal-input kcal-input"
                                    min="0"
                                    step="any"
                                    value={exerciseForm.kcal}
                                    onChange={(e) =>
                                        setExerciseForm((prev) => ({
                                            ...prev,
                                            kcal: e.target.value
                                        }))
                                    }
                                    disabled={isProcessing}
                                />
                            </div>

                            {/* Time of exercise */}
                            <div className="input-with-label">
                                <span className="inline-label">Klockslag:</span>
                                <input
                                    type="time"
                                    id="own-exercise-time"
                                    className="modal-input time-input"
                                    value={exerciseForm.time}
                                    onChange={(e) =>
                                        setExerciseForm((prev) => ({
                                            ...prev,
                                            time: e.target.value
                                        }))
                                    }
                                    disabled={isProcessing}
                                />
                            </div>

                            {/* Optional comment */}
                            <div className="input-with-label">
                                <span className="inline-label">Kommentar:</span>
                                <input
                                    type="text"
                                    id="own-exercise-comment"
                                    className="modal-input comment-input"
                                    value={exerciseForm.comment}
                                    onChange={(e) =>
                                        setExerciseForm((prev) => ({
                                            ...prev,
                                            comment: e.target.value
                                        }))
                                    }
                                    disabled={isProcessing}
                                    placeholder="Valfritt"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="own-exercise-wrapper">
                            {savedExercises.map((ex, index) => {
                                const duration = formatDuration(ex.duration);

                                return (
                                    <div className="own-exercise" key={ex.name + index}>
                                        <span className="own-exercise-info">
                                            <span>
                                                {formatExercisePrefix({
                                                    time: ex.time,
                                                    duration
                                                })}{' '}
                                            </span>
                                            {ex.name || 'Egen kaloriberäknad motion'}
                                        </span>

                                        <span className="own-exercise-kcal">{ex.kcal} kcal</span>
                                    </div>
                                );
                            })}
                        </div>
                    )
                ) : (
                    <ErrorMessage message={error?.message || 'Ett fel uppstod vid sparandet. Försök igen.'} />
                )}
            </div>

            <div className="modal-footer own-exercise">
                <button
                    type="button"
                    className="btn-green bold-title"
                    id="save-own-exercise-btn"
                    disabled={isProcessing || !exerciseForm.kcal}
                    onClick={saveOwnExercise}
                >
                    <ActionStatusText
                        isProcessing={isSaving}
                        isCompleted={!isAddingExercise && isSuccess}
                        defaultLabel="Spara motion"
                        successLabel="Motion sparad!"
                    />
                </button>

                <button
                    type="button"
                    className="border-btn-green bold-title"
                    id="add-own-exercise-btn"
                    onClick={() => {
                        setExerciseForm({
                            name: '',
                            duration: '',
                            kcal: '',
                            time: getNowHHmm(),
                            comment: ''
                        });
                        setIsManualAdd(true);
                    }}
                    disabled={isProcessing || isAddingExercise}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
                        <line x1="8" y1="4" x2="8" y2="12" stroke="currentColor" strokeWidth="2" />
                        <line x1="4" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    Lägg till en till
                </button>
            </div>
        </>
    );
};

export default ModalOwnExercise;
